import { Component, Inject, HostListener, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';

export interface ImageViewerDialogData {
    images: (string | File)[];
    currentIndex: number;
}

@Component({
    selector: 'app-image-viewer-dialog',
    standalone: true,
    imports: [MatDialogModule, MatIconModule, MatButtonModule, CommonModule],
    template: `
        <div class="image-viewer-container">
            <div class="image-viewer-header">
                <button mat-icon-button (click)="close()" class="close-button">
                    <mat-icon>close</mat-icon>
                </button>
            </div>
            <div class="image-viewer-content">
                <button 
                    mat-icon-button 
                    class="nav-button prev-button" 
                    (click)="previousImage()"
                    [disabled]="data.currentIndex === 0">
                    <mat-icon>chevron_left</mat-icon>
                </button>
                
                <div class="image-wrapper">
                    <img [src]="currentImageSrc" [alt]="'Image ' + (data.currentIndex + 1)" class="viewer-image">
                </div>
                
                <button 
                    mat-icon-button 
                    class="nav-button next-button" 
                    (click)="nextImage()"
                    [disabled]="data.currentIndex === data.images.length - 1">
                    <mat-icon>chevron_right</mat-icon>
                </button>
            </div>
            <div class="image-viewer-footer">
                <span class="image-counter">{{ data.currentIndex + 1 }} / {{ data.images.length }}</span>
            </div>
        </div>
    `,
    styles: [`
        .image-viewer-container {
            position: relative;
            width: 90vw;
            max-width: 1200px;
            max-height: 90vh;
            background: #000;
            display: flex;
            flex-direction: column;
        }

        .image-viewer-header {
            position: absolute;
            top: 0;
            right: 0;
            z-index: 10;
            padding: 8px;
        }

        .close-button {
            color: #fff;
            background: rgba(0, 0, 0, 0.5);
        }

        .close-button:hover {
            background: rgba(0, 0, 0, 0.7);
        }

        .image-viewer-content {
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 1;
            position: relative;
            min-height: 400px;
        }

        .image-wrapper {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
        }

        .viewer-image {
            max-width: 100%;
            max-height: 80vh;
            object-fit: contain;
        }

        .nav-button {
            color: #fff;
            background: rgba(0, 0, 0, 0.5);
            width: 48px;
            height: 48px;
            z-index: 10;
        }

        .nav-button:hover:not([disabled]) {
            background: rgba(0, 0, 0, 0.7);
        }

        .nav-button[disabled] {
            opacity: 0.3;
            cursor: not-allowed;
        }

        .image-viewer-footer {
            padding: 16px;
            text-align: center;
            background: rgba(0, 0, 0, 0.7);
        }

        .image-counter {
            color: #fff;
            font-size: 14px;
        }

        ::ng-deep .mat-mdc-dialog-container {
            padding: 0 !important;
        }
    `]
})
export class ImageViewerDialogComponent implements OnDestroy {
    private objectUrls: Map<File, string> = new Map();

    constructor(
        public dialogRef: MatDialogRef<ImageViewerDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ImageViewerDialogData
    ) {}

    get currentImageSrc(): string {
        if (this.data.currentIndex < 0 || this.data.currentIndex >= this.data.images.length) {
            return '';
        }
        
        const image = this.data.images[this.data.currentIndex];
        if (!image) {
            return '';
        }
        
        if (image instanceof File) {
            // Use the File object as the key - File objects are compared by reference
            if (!this.objectUrls.has(image)) {
                const url = URL.createObjectURL(image);
                this.objectUrls.set(image, url);
            }
            return this.objectUrls.get(image)!;
        }
        return image as string;
    }

    ngOnDestroy(): void {
        // Clean up object URLs
        this.objectUrls.forEach(url => URL.revokeObjectURL(url));
        this.objectUrls.clear();
    }

    previousImage(): void {
        if (this.data.currentIndex > 0) {
            this.data.currentIndex--;
        }
    }

    nextImage(): void {
        if (this.data.currentIndex < this.data.images.length - 1) {
            this.data.currentIndex++;
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        if (event.key === 'ArrowLeft') {
            this.previousImage();
        } else if (event.key === 'ArrowRight') {
            this.nextImage();
        } else if (event.key === 'Escape') {
            this.close();
        }
    }
}

